/**
 * Social Lookup Page
 *
 * Workers use this page to search for Instagram/Facebook pages of places.
 * Each place has two buttons that open Google searches. Workers must complete
 * both platforms for a place before moving to the next one (hard lock).
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Card, Table, Select, Switch, Space, Typography, Alert, Button, Tag, App, Tooltip, Input,
} from 'antd';
import {
  InstagramOutlined, FacebookOutlined, CheckCircleFilled, LockOutlined,
  SearchOutlined, FilterOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { usePlaces, useSearchTerms, useCountries } from '@/hooks/usePlaces';
import {
  useWorkerStatus, usePlaceClickStatus, useRecordClick,
} from '@/hooks/useSocialLookup';
import { useSiteAuth } from '@/stores/useSiteAuthStore';
import { useVisibleSearchTerms } from '@/hooks/useSettings';
import type { PlaceListItem, PlaceFilter } from '@/types/place';
import type { SocialPlatform } from '@/types/socialLookup';
import { formatNumber } from '@/utils/formatters';

const { Title, Text, Paragraph } = Typography;

const STORAGE_KEY = 'social-lookup-state';

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function savePersistedState(state: Record<string, unknown>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

export function SocialLookupPage() {
  const { notification } = App.useApp();
  const { isAdmin } = useSiteAuth();
  const persisted = loadPersistedState();

  // Filters
  const [filters, setFilters] = useState<PlaceFilter>(() => ({
    searchTerm: persisted.searchTerm,
    country: persisted.country ?? ['Germany'],
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(persisted.pageSize ?? 50);
  const [hideCompleted, setHideCompleted] = useState(persisted.hideCompleted ?? true);
  const [curatedEnabled, setCuratedEnabled] = useState(persisted.curatedEnabled ?? true);
  const [blacklistEnabled, setBlacklistEnabled] = useState(persisted.blacklistEnabled ?? true);
  const [searchValue, setSearchValue] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Persist state
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  useEffect(() => {
    savePersistedState({
      searchTerm: filters.searchTerm,
      country: filters.country,
      pageSize,
      hideCompleted,
      curatedEnabled,
      blacklistEnabled,
    });
  }, [filters, pageSize, hideCompleted, curatedEnabled, blacklistEnabled]);

  // Visible search terms (non-admin access control)
  const { data: visibleTermsList = [] } = useVisibleSearchTerms();
  const visibleTermsSet = useMemo(() => new Set(visibleTermsList), [visibleTermsList]);

  const effectiveSearchTerm = useMemo(() => {
    if (isAdmin) return filters.searchTerm;
    if (visibleTermsList.length === 0) return ['__none__'];
    if (filters.searchTerm) {
      const userTerms = Array.isArray(filters.searchTerm) ? filters.searchTerm : [filters.searchTerm];
      const restricted = userTerms.filter((t) => visibleTermsSet.has(t));
      return restricted.length > 0 ? restricted : visibleTermsList;
    }
    return visibleTermsList;
  }, [isAdmin, filters.searchTerm, visibleTermsList, visibleTermsSet]);

  // Fetch places
  const {
    data: placesData,
    isLoading,
    isFetching,
  } = usePlaces({
    ...filters,
    searchTerm: effectiveSearchTerm,
    curatedCategoriesOnly: curatedEnabled || undefined,
    nameBlacklistActive: blacklistEnabled || undefined,
    excludeCompletedSocialLookup: hideCompleted || undefined,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });

  const places = placesData?.places || [];
  const total = placesData?.total || 0;

  // Place IDs on current page — for status fetch
  const visiblePlaceIds = useMemo(() => places.map((p) => p.placeId), [places]);

  // Click statuses for visible places
  const { data: placeStatuses = {} } = usePlaceClickStatus(visiblePlaceIds);

  // Worker lock state
  const { data: workerStatus } = useWorkerStatus();
  const lockedPlaceId = workerStatus?.lockedPlaceId || null;
  const lockedPlaceName = workerStatus?.lockedPlaceName || null;
  const lockedPlatforms = workerStatus?.completedPlatforms || [];

  // Record click mutation
  const recordClick = useRecordClick();

  // Filter data for dropdowns
  const searchTermParams = useMemo(() => ({
    curatedCategoriesOnly: curatedEnabled || undefined,
    nameBlacklistActive: blacklistEnabled || undefined,
  }), [curatedEnabled, blacklistEnabled]);
  const { data: allSearchTerms = [] } = useSearchTerms(searchTermParams);

  const searchTerms = useMemo(() => {
    if (isAdmin) return allSearchTerms;
    return allSearchTerms.filter((t) => visibleTermsSet.has(t.searchQuery));
  }, [isAdmin, allSearchTerms, visibleTermsSet]);

  const countryFilterParams = useMemo(() => {
    const { country: _country, ...rest } = filters;
    return {
      ...rest,
      searchTerm: effectiveSearchTerm,
      curatedCategoriesOnly: curatedEnabled || undefined,
      nameBlacklistActive: blacklistEnabled || undefined,
      excludeCompletedSocialLookup: hideCompleted || undefined,
    };
  }, [filters, effectiveSearchTerm, curatedEnabled, blacklistEnabled, hideCompleted]);
  const { data: countries = [] } = useCountries(countryFilterParams);

  // Click handler
  const handleClick = useCallback((place: PlaceListItem, platform: SocialPlatform) => {
    const platformLabel = platform === 'instagram' ? 'Instagram' : 'Facebook';
    const searchQuery = `${place.name} ${place.address || ''} ${platformLabel}`.trim();
    const url = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;

    recordClick.mutate(
      { placeId: place.placeId, platform },
      {
        onSuccess: () => {
          window.open(url, '_blank');
        },
        onError: (error: unknown) => {
          const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
            || 'Failed to record click';
          notification.error({ message: 'Error', description: msg });
        },
      }
    );
  }, [recordClick, notification]);

  // Handle lock banner button click (complete remaining platform)
  const handleLockBannerClick = useCallback(() => {
    if (!lockedPlaceId) return;
    const remainingPlatform: SocialPlatform = lockedPlatforms.includes('instagram') ? 'facebook' : 'instagram';
    const platformLabel = remainingPlatform === 'instagram' ? 'Instagram' : 'Facebook';
    const searchQuery = `${lockedPlaceName || lockedPlaceId} ${platformLabel}`.trim();
    const url = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;

    recordClick.mutate(
      { placeId: lockedPlaceId, platform: remainingPlatform },
      {
        onSuccess: () => {
          window.open(url, '_blank');
        },
        onError: (error: unknown) => {
          const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
            || 'Failed to record click';
          notification.error({ message: 'Error', description: msg });
        },
      }
    );
  }, [lockedPlaceId, lockedPlaceName, lockedPlatforms, recordClick, notification]);

  // Render a platform button
  const renderPlatformButton = useCallback(
    (place: PlaceListItem, platform: SocialPlatform) => {
      const status = placeStatuses[place.placeId];
      const isDone = status?.[platform] ?? false;
      const isLocked = lockedPlaceId !== null && lockedPlaceId !== place.placeId;
      const icon = platform === 'instagram'
        ? <InstagramOutlined />
        : <FacebookOutlined />;

      if (isDone) {
        return (
          <Tooltip title="Already done">
            <Button size="small" type="text" disabled icon={<CheckCircleFilled style={{ color: '#52c41a' }} />} />
          </Tooltip>
        );
      }

      if (isLocked) {
        return (
          <Tooltip title={`Finish ${lockedPlaceName || 'current place'} first`}>
            <Button size="small" type="text" disabled icon={<LockOutlined style={{ color: '#d9d9d9' }} />} />
          </Tooltip>
        );
      }

      return (
        <Tooltip title={`Search ${platform === 'instagram' ? 'Instagram' : 'Facebook'}`}>
          <Button
            size="small"
            type="primary"
            icon={icon}
            loading={recordClick.isPending}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(place, platform);
            }}
          />
        </Tooltip>
      );
    },
    [placeStatuses, lockedPlaceId, lockedPlaceName, recordClick.isPending, handleClick]
  );

  // Table columns
  const columns: ColumnsType<PlaceListItem> = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      ellipsis: true,
      render: (name: string, record) => (
        <a href={record.googleMapsUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
          {name}
        </a>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 100,
    },
    {
      title: 'IG',
      key: 'instagram',
      width: 60,
      align: 'center',
      render: (_: unknown, record: PlaceListItem) => renderPlatformButton(record, 'instagram'),
    },
    {
      title: 'FB',
      key: 'facebook',
      width: 60,
      align: 'center',
      render: (_: unknown, record: PlaceListItem) => renderPlatformButton(record, 'facebook'),
    },
  ], [renderPlatformButton]);

  // Filter change resets pagination
  const updateFilter = (update: Partial<PlaceFilter>) => {
    setFilters((prev) => ({ ...prev, ...update }));
    setPage(1);
  };

  const remainingPlatform: SocialPlatform | null = lockedPlaceId
    ? (lockedPlatforms.includes('instagram') ? 'facebook' : 'instagram')
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>Social Lookup</Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Search for Instagram and Facebook pages of places. Complete both platforms before moving to the next place.
        </Paragraph>
      </div>

      {/* Lock banner */}
      {lockedPlaceId && (
        <Alert
          type="warning"
          showIcon
          icon={<LockOutlined />}
          message={
            <Space>
              <span>
                Complete <strong>{lockedPlaceName || lockedPlaceId}</strong> —{' '}
                {remainingPlatform === 'instagram' ? 'Instagram' : 'Facebook'} still pending
              </span>
              <Button
                type="primary"
                size="small"
                icon={remainingPlatform === 'instagram' ? <InstagramOutlined /> : <FacebookOutlined />}
                loading={recordClick.isPending}
                onClick={handleLockBannerClick}
              >
                Open {remainingPlatform === 'instagram' ? 'Instagram' : 'Facebook'} Search
              </Button>
            </Space>
          }
        />
      )}

      {/* Filters */}
      <Card size="small">
        <Space wrap size={12}>
          <Input.Search
            placeholder="Search places..."
            allowClear
            value={searchValue}
            onChange={(e) => {
              const val = e.target.value;
              setSearchValue(val);
              if (searchTimeout.current) clearTimeout(searchTimeout.current);
              searchTimeout.current = setTimeout(() => {
                updateFilter({ search: val || undefined });
              }, 300);
            }}
            style={{ width: 200 }}
            size="middle"
          />
          <Select
            placeholder="Search Term"
            style={{ width: 200 }}
            allowClear
            value={filters.searchTerm as string | undefined}
            onChange={(val) => updateFilter({ searchTerm: val || undefined })}
            options={searchTerms.map((t) => ({
              value: t.searchQuery,
              label: `${t.searchQuery} (${formatNumber(t.remainingCount)})`,
            }))}
          />
          <Select
            mode="multiple"
            placeholder="Country"
            style={{ width: 200 }}
            maxTagCount={1}
            value={Array.isArray(filters.country) ? filters.country : []}
            onChange={(val) => updateFilter({ country: val.length > 0 ? val : undefined })}
            options={countries.map((c) => ({
              value: c.country,
              label: `${c.country} (${formatNumber(c.count)})`,
            }))}
          />
          <Space size={4}>
            <FilterOutlined style={{ color: 'var(--color-text-tertiary)' }} />
            <Switch
              size="small"
              checked={hideCompleted}
              onChange={(v) => { setHideCompleted(v); setPage(1); }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>Hide completed</Text>
          </Space>
          <Space size={4}>
            <Switch
              size="small"
              checked={curatedEnabled}
              onChange={(v) => { setCuratedEnabled(v); setPage(1); }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>Curated categories</Text>
          </Space>
          <Space size={4}>
            <Switch
              size="small"
              checked={blacklistEnabled}
              onChange={(v) => { setBlacklistEnabled(v); setPage(1); }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>Name blacklist</Text>
          </Space>
          {total > 0 && (
            <Tag color="blue">{formatNumber(total)} places</Tag>
          )}
        </Space>
      </Card>

      {/* Table */}
      <Card size="small" styles={{ body: { padding: 0 } }}>
        <Table<PlaceListItem>
          columns={columns}
          dataSource={places}
          rowKey="placeId"
          loading={isLoading || isFetching}
          size="small"
          scroll={{ x: 930 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            onChange: (p, ps) => {
              setPage(p);
              if (ps !== pageSize) setPageSize(ps);
            },
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${formatNumber(t)}`,
          }}
        />
      </Card>
    </div>
  );
}

export default SocialLookupPage;
